import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNavigate } from 'react-router-dom';
import {
  BRAND_GREEN,
  FONT_BODY,
  FONT_HEAVY,
  loadManagedPortfolios,
  money,
} from './managedPortfoliosData';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: '110px 24px 70px',
    fontFamily: FONT_BODY,
  },
  shell: { maxWidth: 1380, margin: '0 auto' },
  back: {
    color: `${theme.palette.text.secondary} !important`,
    textTransform: 'none !important',
    paddingLeft: '0 !important',
  },
  headingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
    alignItems: 'flex-end',
    margin: '22px 0 28px',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  eyebrow: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: FONT_HEAVY,
    fontSize: 38,
    margin: '5px 0 6px',
    lineHeight: 1.15,
    [theme.breakpoints.down('sm')]: { fontSize: 30 },
  },
  subtitle: { margin: 0, color: theme.palette.text.secondary, maxWidth: 670 },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 22,
    [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  stat: { background: BRAND_GREEN, color: '#fff', borderRadius: 14, padding: 20 },
  statLabel: { color: '#B9D4C8', fontSize: 13 },
  statValue: {
    fontFamily: FONT_HEAVY,
    fontSize: 27,
    fontWeight: 700,
    marginTop: 5,
  },
  panel: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: 'hidden',
  },
  toolbar: {
    padding: 18,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  search: { minWidth: 230 },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.1fr 1fr .8fr 40px',
    padding: '11px 18px',
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.1fr 1fr .8fr 40px',
    padding: '15px 18px',
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: '.15s',
    '&:hover': { background: theme.palette.action.hover },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr auto',
      rowGap: 12,
    },
  },
  client: { display: 'flex', alignItems: 'center', gap: 11 },
  avatar: {
    background: `${theme.palette.primary.main} !important`,
    color: '#fff !important',
    fontWeight: '700 !important',
  },
  name: { fontWeight: 700, color: theme.palette.text.primary },
  muted: { color: theme.palette.text.secondary, fontSize: 13 },
  value: { fontWeight: 700, color: theme.palette.text.primary },
  chevron: { color: theme.palette.text.secondary },
  state: { padding: 48, textAlign: 'center', color: theme.palette.text.secondary },
}));

export default function ManagedPortfoliosDemo() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All products');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPortfolios = async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await loadManagedPortfolios());
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          'Unable to load managed portfolios. Please sign in again or try later.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortfolios();
  }, []);

  const visibleRows = rows.filter(
    (row) =>
      (filter === 'All products' || row.product === filter) &&
      `${row.clientName} ${row.plan}`
        .toLowerCase()
        .includes(query.toLowerCase())
  );
  const totalValue = useMemo(
    () => rows.reduce((sum, row) => sum + row.value, 0),
    [rows]
  );
  const awaiting = useMemo(
    () => rows.filter((row) => row.status !== 'On track').length,
    [rows]
  );

  const openPortfolio = (row) =>
    navigate(
      `/referral/managed-portfolios/${encodeURIComponent(row.portfolioId)}`
    );

  return (
    <Box className={classes.page}>
      <Box className={classes.shell}>
        <Button
          className={classes.back}
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/referral')}
        >
          Back to Referral
        </Button>
        <Box className={classes.headingRow}>
          <Box>
            <div className={classes.eyebrow}>Referrer workspace</div>
            <h1 className={classes.title}>Managed Portfolios</h1>
            <p className={classes.subtitle}>
              Monitor the Smart Crypto and Treasury portfolios of clients who
              joined through your referral. Open one to review it.
            </p>
          </Box>
        </Box>

        <Box className={classes.stats}>
          <Box className={classes.stat}>
            <PeopleAltRoundedIcon />
            <div className={classes.statLabel}>Managed portfolios</div>
            <div className={classes.statValue}>{rows.length}</div>
          </Box>
          <Box className={classes.stat}>
            <div className={classes.statLabel}>Assets monitored</div>
            <div className={classes.statValue}>{money(totalValue)}</div>
          </Box>
          <Box className={classes.stat}>
            <TrendingUpRoundedIcon />
            <div className={classes.statLabel}>Active portfolios</div>
            <div className={classes.statValue}>{rows.length}</div>
          </Box>
          <Box className={classes.stat}>
            <WarningAmberRoundedIcon />
            <div className={classes.statLabel}>Awaiting approval</div>
            <div className={classes.statValue}>{awaiting}</div>
          </Box>
        </Box>

        <Box className={classes.panel}>
          <Box className={classes.toolbar}>
            <TextField
              className={classes.search}
              size="small"
              placeholder="Search referrals"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
            />
            <Select
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="All products">All products</MenuItem>
              <MenuItem value="Smart Crypto">Smart Crypto</MenuItem>
              <MenuItem value="Treasury">Treasury</MenuItem>
            </Select>
          </Box>
          <Box className={classes.tableHead}>
            <span>Referral</span>
            <span>Portfolio</span>
            <span>Value</span>
            <span>Status</span>
            <span />
          </Box>
          {loading && (
            <Box className={classes.state}>
              <CircularProgress size={28} />
              <p>Loading authorized referral portfolios…</p>
            </Box>
          )}
          {!loading && error && (
            <Box className={classes.state}>
              <WarningAmberRoundedIcon color="warning" />
              <p>{error}</p>
              <Button onClick={loadPortfolios}>Try again</Button>
            </Box>
          )}
          {!loading && !error && visibleRows.length === 0 && (
            <Box className={classes.state}>
              <PeopleAltRoundedIcon />
              <h3>No managed portfolios yet</h3>
              <p>
                Referred clients must grant you portfolio access before their
                Smart Crypto or Treasury holdings appear here.
              </p>
            </Box>
          )}
          {!loading &&
            !error &&
            visibleRows.map((row) => (
              <Box
                key={row.rowId}
                className={classes.row}
                onClick={() => openPortfolio(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openPortfolio(row);
                }}
              >
                <Box className={classes.client}>
                  <Avatar className={classes.avatar}>{row.initials}</Avatar>
                  <Box>
                    <div className={classes.name}>{row.clientName}</div>
                    <div className={classes.muted}>{row.product}</div>
                  </Box>
                </Box>
                <Box>
                  <div className={classes.name}>{row.plan}</div>
                  <div className={classes.muted}>{row.risk}</div>
                </Box>
                <Box>
                  <div className={classes.value}>{money(row.value)}</div>
                  <div className={classes.muted}>{row.status}</div>
                </Box>
                <Chip
                  size="small"
                  color={row.status === 'On track' ? 'success' : 'warning'}
                  variant="outlined"
                  label={row.status}
                />
                <ChevronRightRoundedIcon className={classes.chevron} />
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
}
